import { useCallback } from 'react'

import { Briefcase, Plus } from '@gravity-ui/icons'
import { Button, cn, Spinner, Table, toast } from '@heroui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { useTimesheetStore } from '@/store/timesheet.store'

import { TimesheetActivityTableRow } from './TimesheetActivityTableRow'

interface DayActivitiesCardProps {
  isReadOnly?: boolean;
  showErrors?: boolean;
}

export function DayActivitiesCard({ isReadOnly = false, showErrors = false }: DayActivitiesCardProps) {
  const { t, i18n } = useTranslation()
  const {
    selectedDay,
    filters,
    report,
    isLoading: isGlobalLoading,
    removeActivity,
    addActivity,
    updateActivity,
  } = useTimesheetStore()

  // Filter activities for the specific day (Option B: within date range), or show all if no day selected
  const activities = selectedDay
    ? (report?.Items || []).filter(item => {
      if (!item?.StartDate || !item.EndDate) return false

      // Convert to simple YYYY-MM-DD for comparison
      const start = item.StartDate.split('T')[0]
      const end = item.EndDate.split('T')[0]

      const targetDateStr = `${selectedDay.date.getFullYear()}-${String(selectedDay.date.getMonth() + 1).padStart(2, '0')}-${String(selectedDay.date.getDate()).padStart(2, '0')}`

      return targetDateStr >= start && targetDateStr <= end
    })
    : (report?.Items || [])

  const handleAddActivity = useCallback(() => {
    let defaultDateStr = ''
    if (selectedDay) {
      const d = selectedDay.date
      defaultDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    } else {
      defaultDateStr = `${filters.year}-${String(filters.month).padStart(2, '0')}-01`
    }
    addActivity({
      StartDate: defaultDateStr,
      EndDate: defaultDateStr,
      Activity: '',
    })
  }, [selectedDay, filters, addActivity])

  const getUncompletedDays = () => {
    if (!filters.driverID) return []
    const daysInMonth = new Date(filters.year, filters.month, 0).getDate()
    const uncompleted: number[] = []

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${filters.year}-${String(filters.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const isCovered = (report?.Items || []).some(item => {
        if (!item?.StartDate || !item?.EndDate) return false
        const start = item.StartDate.split('T')[0]
        const end = item.EndDate.split('T')[0]
        return dateStr >= start && dateStr <= end
      })
      if (!isCovered) {
        uncompleted.push(d)
      }
    }
    return uncompleted
  }

  const formatRanges = (days: number[]) => {
    if (days.length === 0) return ''
    const ranges: string[] = []
    let start = days[0]
    let prev = days[0]

    for (let i = 1; i <= days.length; i++) {
      const curr = days[i]
      if (curr === prev + 1) {
        prev = curr
      } else {
        const startStr = String(start).padStart(2, '0')
        if (start === prev) {
          ranges.push(startStr)
        } else {
          const prevStr = String(prev).padStart(2, '0')
          ranges.push(`${startStr}-${prevStr}`)
        }
        start = curr
        prev = curr
      }
    }
    return ranges.join(',')
  }

  const handleAutofillEmptyDays = useCallback(() => {
    if (!filters.driverID) return
    const uncompletedDays = getUncompletedDays()

    if (uncompletedDays.length === 0) {
      toast.info(t('timesheet.toast.all_days_completed', 'Все дни месяца уже заполнены'))
      return
    }

    const newItems: any[] = []
    let start = uncompletedDays[0]
    let prev = uncompletedDays[0]

    const pad = (num: number) => String(num).padStart(2, '0')

    for (let i = 1; i <= uncompletedDays.length; i++) {
      const curr = uncompletedDays[i]
      if (curr === prev + 1) {
        prev = curr
      } else {
        const startStr = `${filters.year}-${pad(filters.month)}-${pad(start)}`
        const endStr = `${filters.year}-${pad(filters.month)}-${pad(prev)}`
        newItems.push({
          StartDate: startStr,
          EndDate: endStr,
          Activity: 'ub',
        })
        start = curr
        prev = curr
      }
    }

    useTimesheetStore.setState(state => {
      const currentReport = state.report || { Items: [] } as any
      return {
        report: {
          ...currentReport,
          Items: [...(currentReport.Items || []), ...newItems]
        }
      }
    })
  }, [filters, report?.Items, t])

  return (
    <Card className="h-full flex flex-col" variant='transparent'>
      <CardHeader className="flex-row items-center justify-between gap-1">
        <div className="flex flex-col gap-1">
          <div className="text-lg font-bold">{t('timesheet.activities')}</div>
          <div className="text-sm text-foreground/60">
            {selectedDay
              ? selectedDay.date.toLocaleDateString(i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
              : `${String(filters.month).padStart(2, '0')}.${filters.year}`
            }
          </div>
        </div>
        {!isReadOnly && filters.driverID && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              onClick={handleAutofillEmptyDays}
            >
              {t('timesheet.autofill_ub', 'Заполнить пустые')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              onClick={handleAddActivity}
            >
              <Plus className="size-4 mr-1" />
              {t('timesheet.add_activity', 'Добавить активность')}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto px-2 pb-6 pt-0 relative">
        <AnimatePresence mode="wait">
          {!filters.driverID ? (
            <motion.div
              key="empty-driver"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className="h-full flex items-center justify-center"
            >
              <EmptyState
                icon={<Briefcase className="size-8" />}
                title={t('timesheet.select_driver_prompt')}
                description={''}
              />
            </motion.div>
          ) : isGlobalLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex items-center justify-center"
            >
              <Spinner color="current" />
            </motion.div>
          ) : isReadOnly && activities.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className="h-full flex items-center justify-center"
            >
              <EmptyState
                icon={<Briefcase className="size-8" />}
                title={t('timesheet.no_activities')}
                description={''}
              />
            </motion.div>
          ) : (
            <motion.div
              key="table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <div className="overflow-x-auto w-full">
                <Table className="w-full">
                  <Table.ScrollContainer className={isReadOnly ? "max-h-[calc(85vh-140px)]" : "max-h-[calc(75vh-170px)]"}>
                    <Table.Content aria-label={t('timesheet.activities_table', 'Таблица активностей')}>
                      <Table.Header className="sticky top-0 z-10">
                        <Table.Column style={{ minWidth: 280 }}>{t('timesheet.date_range', 'Период')}</Table.Column>
                        <Table.Column style={{ minWidth: 180 }} className="w-full">{t('timesheet.activity', 'Активность')}</Table.Column>
                        {!isReadOnly && <Table.Column style={{ width: 50 }}> </Table.Column>}
                      </Table.Header>
                      <Table.Body>
                        {(isReadOnly ? activities : (report?.Items || [])).map((item, index) => (
                          <TimesheetActivityTableRow
                            key={index}
                            index={index}
                            item={item}
                            isReadOnly={isReadOnly}
                            onChange={updateActivity}
                            onDelete={() => removeActivity(index)}
                            showErrors={showErrors}
                          />
                        ))}
                        {!isReadOnly && (
                          <Table.Row key="add-new-row-btn">
                            <Table.Cell colSpan={3} className="pt-4 px-2">
                              <Button
                                variant="ghost"
                                className="w-full text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                onClick={handleAddActivity}
                              >
                                <Plus className="size-4 mr-2" />
                                {t('timesheet.add_activity', 'Добавить активность')}
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table.Content>
                  </Table.ScrollContainer>
                  {!isReadOnly && filters.driverID && (
                    <Table.Footer>
                      <div className="flex w-full items-center justify-between px-2 py-1 text-xs">
                        <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400">
                          <span>{t('timesheet.uncompleted_days', 'Незаполненные дни')}:</span>
                          <span className={cn(
                            "font-semibold",
                            getUncompletedDays().length > 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"
                          )}
                          >
                            {formatRanges(getUncompletedDays()) || t('timesheet.all_days_completed', 'все дни заполнены')}
                          </span>
                        </div>
                      </div>
                    </Table.Footer>
                  )}
                </Table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
